import { useParams, Link } from 'react-router-dom';
import { categories, getArticlesByCategory } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = getArticlesByCategory(slug || '');

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="font-heading text-3xl text-foreground-900 mb-4">
          分类未找到
        </h1>
        <Link
          to="/"
          className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <title>{category.name} | 六合宝典</title>
      <meta name="description" content={category.description} />
      <meta
        name="keywords"
        content={`六合宝典${category.name},${category.name}文章,${category.name}指南`}
      />
      <link
        rel="canonical"
        href={`https://liuhebaodian.com/category/${category.slug}`}
      />
      <meta property="og:title" content={`${category.name} | 六合宝典`} />
      <meta property="og:description" content={category.description} />
      <meta property="og:type" content="website" />
      <meta
        property="og:url"
        content={`https://liuhebaodian.com/category/${category.slug}`}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        {/* Category Header */}
        <div className="mb-10 mt-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100">
              <i className={`${category.icon} text-2xl text-primary-600`}></i>
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                {category.articleCount} 篇文章
              </p>
            </div>
          </div>
          <p className="text-foreground-600 leading-relaxed max-w-3xl">
            {category.description}
          </p>
        </div>

        {/* Category Switcher */}
        <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                cat.slug === slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {/* Articles List */}
        {categoryArticles.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group flex flex-col sm:flex-row gap-4 bg-background-50 rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="flex-shrink-0 w-full sm:w-44 h-32 rounded-lg overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    title={`${article.title} - 六合宝典`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs text-foreground-400">
                      {article.publishDate}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {article.readTime}
                    </span>
                  </div>
                  <h2 className="font-heading text-base text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-md bg-secondary-100 text-xs text-secondary-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-inbox-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无文章</p>
          </div>
        )}
      </div>
    </>
  );
}